import { z } from 'zod';

/**
 * Every response uses this shape. No endpoint returns a bare array — adding a
 * field to `meta` later must never be a breaking change for a client that
 * assumed the top level was the payload.
 *
 * See docs/03-API-SPECIFICATION.md §1.1.
 */

export const ResponseMetaSchema = z.object({
  requestId: z.string(),
  serverTime: z.string().datetime({ offset: true }),
  pagination: z
    .object({
      perPage: z.number().int().positive(),
      nextCursor: z.string().nullable(),
      prevCursor: z.string().nullable(),
      hasMore: z.boolean(),
      total: z.number().int().nonnegative().optional(),
    })
    .optional(),
});
export type ResponseMeta = z.infer<typeof ResponseMetaSchema>;

export const ApiErrorSchema = z.object({
  code: z.string(),
  details: z.record(z.unknown()).optional(),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;

/** Success envelope. Generic over the payload schema. */
export const successEnvelope = <T extends z.ZodTypeAny>(data: T) =>
  z.object({
    success: z.literal(true),
    message: z.string(),
    data,
    meta: ResponseMetaSchema,
  });

export const ErrorEnvelopeSchema = z.object({
  success: z.literal(false),
  message: z.string(),
  error: ApiErrorSchema,
  /** Field-keyed; present only on 422. */
  errors: z.record(z.array(z.string())).optional(),
  meta: ResponseMetaSchema,
});
export type ErrorEnvelope = z.infer<typeof ErrorEnvelopeSchema>;

export interface SuccessEnvelope<T> {
  success: true;
  message: string;
  data: T;
  meta: ResponseMeta;
}

export type Envelope<T> = SuccessEnvelope<T> | ErrorEnvelope;
